import { useEffect, useState } from 'react';
import { Link, useLocation, useRoute } from 'wouter';
import {
  api, type KnowledgeArticle, type KnowledgeFields, type KnowledgeVersion, type KnowledgeRow, type TemplateRow,
} from '../lib/api';
import type { ConfirmRequest } from '../components/Confirm';
import { useResource } from '../lib/use-resource';
import { dayLocal } from '../lib/time';
import {
  changed, defaultArticle, EMPTY, fieldsOf, filterArticles, INSTRUCTIONS_SLUG, problem, reconcile, supersedeOptions, viewOf,
  type View,
} from '../lib/knowledge';
import { DrafterPanel } from './DrafterPanel';
import { TemplatePanel } from './TemplatePanel';
import { ALL, families, filterTemplates, STATE_LABEL, type StateFilter, type TemplateFilter } from '../lib/templates';
import { takeHandoff, withLine } from '../lib/handoff';
import './knowledge.css';

/**
 * What the drafter knows (fix-22).
 *
 * Every article that is not a website page is yours to create, edit and retire here — never by migration again.
 * A save keeps the article as it was, with who and why, and the next draft reads the new text: the desk re-cuts the
 * article's sections before it answers. Website pages are read-only: the weekly crawl owns them, so change the page.
 *
 * "Supersedes" names an older article this one replaces. While this one is live, the older one is out of the
 * drafter's search, so the two never contradict each other on a card.
 *
 * fix-57: the screen opens on the Drafter instructions — how every draft is written — with the rest of the prompt
 * read-only below them and the model beside it.
 *
 * spec 014 Stage 1: a fourth tab, Templates — the emails the rules send, read-only, opened at /knowledge/templates/:id.
 */
export function Knowledge({ onConfirm, operator }: { onConfirm: (r: ConfirmRequest) => void; operator: boolean }) {
  const list = useResource<{ articles: KnowledgeRow[] }>(() => api.knowledge.list());
  const [, isNew] = useRoute('/knowledge/new');
  const [, match] = useRoute<{ id: string }>('/knowledge/:id');
  const [, tmplMatch] = useRoute<{ id: string }>('/knowledge/templates/:id');
  const templates = useResource<{ templates: TemplateRow[] }>(() => api.templates.list());
  const openTemplate = tmplMatch ? Number(tmplMatch.id) : null;
  const [view, setView] = useState<View | 'templates'>(openTemplate !== null ? 'templates' : 'yours');
  const [query, setQuery] = useState('');

  const rows = list.data?.articles ?? [];
  const selected = !isNew && match ? Number(match.id) : (isNew ? null : defaultArticle(rows));
  // Open an article and the list shows the tab it lives on.
  const openRow = openTemplate === null ? rows.find((r) => r.id === selected) : undefined;
  useEffect(() => { if (openRow) setView(viewOf(openRow)); }, [openRow?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const shown = view === 'templates' ? [] : filterArticles(rows, view, query);
  const count = (v: View) => rows.filter((a) => viewOf(a) === v).length;

  return (
    <main className="board knowledge">
      <div className="knowledge-head">
        <div>
          <div className="mono eyebrow">Knowledge</div>
          <h1>What the drafter knows</h1>
        </div>
        <Link href="/knowledge/new" className="btn-primary knowledge-new">New article</Link>
      </div>

      <div className="knowledge-grid">
        <section className="panel knowledge-list">
          <div className="knowledge-tabs" role="tablist">
            {(['yours', 'website', 'retired'] as View[]).map((v) => (
              <button key={v} type="button" role="tab" aria-selected={view === v}
                className={`knowledge-tab ${view === v ? 'is-current' : ''}`} onClick={() => setView(v)}>
                {v === 'yours' ? 'Yours' : v === 'website' ? 'Website' : 'Retired'} <span className="mono">{count(v)}</span>
              </button>
            ))}
            <button type="button" role="tab" aria-selected={view === 'templates'}
              className={`knowledge-tab ${view === 'templates' ? 'is-current' : ''}`} onClick={() => setView('templates')}>
              Templates <span className="mono">{templates.data?.templates.length ?? '…'}</span>
            </button>
          </div>
          {view === 'templates'
            ? <TemplateList rows={templates.data?.templates ?? []} open={openTemplate} error={templates.error?.message}
                loading={templates.loading} />
            : <>
          <input className="knowledge-search" type="search" placeholder="Search titles" value={query}
            onChange={(e) => setQuery(e.target.value)} aria-label="Search titles" />
          {list.error && <p className="list-error">{list.error.message}</p>}
          <ul className="knowledge-rows">
            {shown.map((a) => (
              <li key={a.id}>
                <Link href={`/knowledge/${a.id}`} className={`knowledge-row ${selected === a.id ? 'is-current' : ''}`}>
                  <span className="knowledge-row-title">{a.title}</span>
                  <span className="mono knowledge-row-meta">
                    {a.always_on && <span className="knowledge-badge">in every draft</span>}
                    {a.superseded_by && <span className="knowledge-badge is-muted">superseded</span>}
                    {a.audience} · {a.weight} · {a.updated_at ? dayLocal(a.updated_at) : '—'}
                  </span>
                </Link>
              </li>
            ))}
            {!shown.length && !list.loading && <li className="list-empty">Nothing here.</li>}
          </ul>
            </>}
        </section>

        {openTemplate !== null ? <TemplatePanel key={openTemplate} id={openTemplate} operator={operator} onSaved={() => templates.refetch()} />
          : isNew || selected !== null
          ? <Editor key={isNew ? 'new' : selected!} id={isNew ? null : selected} rows={rows}
              onSaved={() => list.refetch()} onConfirm={onConfirm} />
          : <section className="panel knowledge-empty"><p className="list-empty">Choose an article, or start a new one.</p></section>}
      </div>
    </main>
  );
}

/** The templates, filtered by state, family and a search of their words (TMPL-02, TMPL-03). */
function TemplateList({ rows, open, error, loading }: {
  rows: TemplateRow[]; open: number | null; error?: string; loading: boolean;
}) {
  const [f, setF] = useState<TemplateFilter>(ALL);
  const shown = filterTemplates(rows, f);
  return (
    <>
      <div className="template-filters">
        <select value={f.state} onChange={(e) => setF({ ...f, state: e.target.value as StateFilter })} aria-label="On or off">
          <option value="all">On, off and unsent</option>
          {(['on', 'off', 'none'] as const).map((s) => <option key={s} value={s}>{STATE_LABEL[s]}</option>)}
        </select>
        <select value={f.family} onChange={(e) => setF({ ...f, family: e.target.value })} aria-label="Family">
          <option value="">Every family</option>
          {families(rows).map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
      </div>
      <input className="knowledge-search" type="search" placeholder="Search subjects and bodies" value={f.query}
        onChange={(e) => setF({ ...f, query: e.target.value })} aria-label="Search subjects and bodies" />
      {error && <p className="list-error">{error}</p>}
      <ul className="knowledge-rows">
        {shown.map((t) => (
          <li key={t.id}>
            <Link href={`/knowledge/templates/${t.id}`} className={`knowledge-row ${open === t.id ? 'is-current' : ''}`}>
              <span className="knowledge-row-title"><span className="mono">T{t.id}</span> {t.subject ?? '(no subject)'}</span>
              <span className="mono knowledge-row-meta">
                <span className={`knowledge-badge ${t.state === 'on' ? '' : 'is-muted'}`}>{STATE_LABEL[t.state]}</span>
                {t.problems.length > 0 && <span className="knowledge-badge is-warn">{t.problems.length} problem{t.problems.length === 1 ? '' : 's'}</span>}
                {t.family ?? 'no row'} · {t.sent_30d} in 30 days
              </span>
            </Link>
          </li>
        ))}
        {!shown.length && !loading && <li className="list-empty">Nothing here.</li>}
      </ul>
    </>
  );
}

function Editor({ id, rows, onSaved, onConfirm }: {
  id: number | null; rows: KnowledgeRow[]; onSaved: () => void; onConfirm: (r: ConfirmRequest) => void;
}) {
  const [, navigate] = useLocation();
  const one = useResource<{ article: KnowledgeArticle; versions: KnowledgeVersion[] } | null>(
    () => (id === null ? Promise.resolve(null) : api.knowledge.get(id)));
  const a = one.data?.article ?? null;
  const [f, setF] = useState<KnowledgeFields>({ ...EMPTY });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // The article the form was loaded from, and a newer saved copy held back because the form has edits.
  const [base, setBase] = useState<KnowledgeArticle | null>(null);
  const [newer, setNewer] = useState<KnowledgeArticle | null>(null);
  const load = (x: KnowledgeArticle | null) => { setF(fieldsOf(x)); setBase(x); setNewer(null); };
  // fix-73: text sent from the Monday edits card, filled in and left for him to read and Save.
  const [handed, setHanded] = useState<string | null>(null);
  useEffect(() => {
    if (id !== null) return;
    const h = takeHandoff({ slug: null, isNew: true });
    if (h?.kind === 'knowledge') {
      setF({ ...EMPTY, title: h.title, body_md: h.body, reason: h.reason });
      setHanded('A new article from a fact you added to a draft. Read it, then Create — nothing is saved until you do.');
    }
  }, [id]);
  useEffect(() => {
    const r = reconcile(base, f, a);
    if (r === 'load') {
      load(a);
      const h = a ? takeHandoff({ slug: a.slug, isNew: false }) : null;
      if (h?.kind === 'rule') {
        setF({ ...fieldsOf(a), body_md: withLine(a!.body_md, h.line), reason: h.reason });
        setHanded('A line drafted from your edits is added at the end. Read it, then Save — the save is refused, as any '
          + 'is, if it breaks a rule.');
      }
    } else if (r === 'notice') setNewer(a);
    // Only a new fetch decides; typing must not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [a]);

  if (id !== null && !a) {
    return <section className="panel knowledge-editor"><p className="list-empty">{one.error?.message ?? 'Reading the article…'}</p></section>;
  }
  const set = <K extends keyof KnowledgeFields>(k: K, v: KnowledgeFields[K]) => setF((x) => ({ ...x, [k]: v }));
  const readOnly = a !== null && (!a.editable || a.status === 'retired');
  const instructions = a?.slug === INSTRUCTIONS_SLUG; // the desk keeps its title, audience and supersedes; never retired
  const blocked = problem(f, id);
  const dirty = changed(f, base ?? a);

  const save = async () => {
    setBusy(true); setError(null);
    try {
      if (id === null) {
        const r = await api.knowledge.create(f);
        onSaved(); navigate(`/knowledge/${r.article.id}`);
      } else {
        await api.knowledge.save(id, f);
        onSaved(); one.refetch();
      }
    } catch (e) { setError((e as Error).message); }
    setBusy(false);
  };
  const retire = () => onConfirm({
    title: `Retire "${a!.title}"?`,
    detail: 'It leaves the drafter\'s search at once. The article and every version stay in the history.',
    confirmLabel: 'Retire', tone: 'danger', askReason: true, reasonRequired: true,
    reasonLabel: 'Why (kept with the version)',
    onConfirm: async (reason) => {
      try { await api.knowledge.retire(a!.id, reason); onSaved(); one.refetch(); } catch (e) { setError((e as Error).message); }
    },
  });

  return (
    <section className="panel knowledge-editor">
      {instructions && (
        <p className="knowledge-note"><strong>How every draft is written.</strong> The drafter&rsquo;s voice and house
          rules. A save reaches the next draft. It is refused if it would let a draft break a body-check rule: a rule&rsquo;s
          first line stays word for word, and an example the check would stop must be marked with &ldquo;not&rdquo;. The
          Classes screen counts drafts from each save.</p>
      )}
      {a?.always_on && !instructions && (
        <p className="knowledge-note"><strong>In every draft.</strong> This article is part of the system prompt every
          draft starts from: a save changes all of them, and the next draft pays one cache miss. It is refused, as the
          Drafter instructions are, if an example would let a draft break a body-check rule (mark one with
          &ldquo;not&rdquo;), if it offers to do setup, or if it gives an activation time.</p>
      )}
      {a?.superseded_by && (
        <p className="knowledge-note">Superseded by <Link href={`/knowledge/${a.superseded_by.id}`}>{a.superseded_by.title}</Link>:
          out of the drafter&rsquo;s search while that one is live.</p>
      )}
      {a && !a.editable && (
        <p className="knowledge-note">A website page — read-only here; the weekly crawl owns it. Change the page itself:{' '}
          {a.url && <a href={a.url} target="_blank" rel="noreferrer">open the page ↗</a>}</p>
      )}
      {a?.status === 'retired' && <p className="knowledge-note">Retired. Kept for the history; not read by the drafter.</p>}
      {handed && <p className="knowledge-note"><strong>From your edits card.</strong> {handed}</p>}
      {newer && (
        <p className="knowledge-note"><strong>Saved elsewhere since you opened it</strong>
          {newer.updated_at ? ` (${dayLocal(newer.updated_at)})` : ''}. Your edits are still here; a Save replaces that
          copy.{' '}
          <button type="button" className="btn-secondary" onClick={() => load(newer)}>Discard my edits and load it</button></p>
      )}

      <label className="knowledge-field">
        <span className="mono">Title</span>
        <input value={f.title} onChange={(e) => set('title', e.target.value)} disabled={readOnly || instructions} maxLength={200} />
      </label>
      <div className="knowledge-row-fields">
        <label className="knowledge-field">
          <span className="mono">Audience</span>
          <select value={f.audience} onChange={(e) => set('audience', e.target.value as KnowledgeFields['audience'])} disabled={readOnly || instructions}>
            <option value="internal">internal</option>
            <option value="public">public</option>
          </select>
        </label>
        <label className="knowledge-field">
          <span className="mono">Weight</span>
          <input type="number" min={0} max={300} value={f.weight} disabled={readOnly}
            onChange={(e) => set('weight', Math.round(Number(e.target.value)))} />
        </label>
        <label className="knowledge-field knowledge-supersedes">
          <span className="mono">Supersedes</span>
          <select value={f.supersedes_id ?? ''} disabled={readOnly || instructions}
            onChange={(e) => set('supersedes_id', e.target.value ? Number(e.target.value) : null)}>
            <option value="">— nothing —</option>
            {supersedeOptions(rows, id).map((o) => <option key={o.id} value={o.id}>{o.title}</option>)}
          </select>
        </label>
      </div>
      <label className="knowledge-field knowledge-body">
        <span className="mono">Body</span>
        <textarea value={f.body_md} onChange={(e) => set('body_md', e.target.value)} disabled={readOnly} />
      </label>

      {!readOnly && (
        <div className="knowledge-save">
          <input className="knowledge-reason" placeholder="Why — kept with the version (required)" value={f.reason}
            onChange={(e) => set('reason', e.target.value)} aria-label="Reason for this save" />
          <button type="button" className="btn-primary" disabled={busy || !dirty || blocked !== null} onClick={save}>
            {id === null ? 'Create' : 'Save'}</button>
          {a && !instructions && <button type="button" className="btn-reject" onClick={retire}>Retire…</button>}
        </div>
      )}
      {!readOnly && dirty && blocked && <p className="mono knowledge-blocked">{blocked}</p>}
      {error && <p className="list-error">{error}</p>}

      {one.data?.versions?.length ? <History versions={one.data.versions} open={instructions} /> : null}
      {instructions && <DrafterPanel key={a!.updated_at ?? ''} onConfirm={onConfirm} />}
    </section>
  );
}

function History({ versions, open = false }: { versions: KnowledgeVersion[]; open?: boolean }) {
  return (
    <details className="knowledge-history" open={open}>
      <summary className="mono">History · {versions.length} version{versions.length === 1 ? '' : 's'}</summary>
      <ol>
        {versions.map((v) => (
          <li key={v.id}>
            <details>
              <summary>
                <span className="mono">{dayLocal(v.created_at)}</span> · {v.changed_by ?? 'unknown'} — {v.reason ?? 'no reason recorded'}
              </summary>
              {v.body_md != null ? <pre className="knowledge-old">{v.body_md}</pre> : <p className="list-empty">Created here.</p>}
            </details>
          </li>
        ))}
      </ol>
    </details>
  );
}
